/**
 * qaFeedback — QA 피드백 모드 공통 게이트 · 설정 · 상태 스토어
 *
 * QA 검증팀이 dev 서버에서 버그를 발견하면 앱 안에서 캡처→주석→제출로 끝내고,
 * 결과가 지정 채널의 Tasks 보드에 태스크 카드로 등록되는 체계의 진입점.
 *
 * 운영(nextpod-ai) 빌드에서는 IS_QA_ELIGIBLE 이 컴파일 타임 상수(false)로 접혀
 * 관련 분기·컴포넌트가 DCE(Dead Code Elimination)로 제거된다. (EnvVersionBadge 패턴)
 * → QA 전용 코드는 반드시 `IS_QA_ELIGIBLE && ...` 게이트 뒤에 두어야 운영에 실리지 않는다.
 */
import qaComm from './qa-comm.json';

/** 운영 프로젝트 여부 — Vite 가 빌드 시 문자열 리터럴로 치환 → 컴파일 타임 상수화 */
export const IS_PROD = import.meta.env.VITE_FIREBASE_PROJECT_ID === 'nextpod-ai';

/** QA 피드백 모드 사용 가능 빌드 (dev/dev-test 전용). 운영 빌드에서 false 로 접힘 → DCE */
export const IS_QA_ELIGIBLE = !IS_PROD;

export interface QaCommTarget {
  workspaceId: string;
  feedbackChannelId: string;
}

/**
 * 현재 빌드 환경(dev/prod)에 해당하는 QA 소통 채널 설정.
 * 채널 변경은 `qa-comm.json` 한 곳만 수정하면 된다.
 */
export function getQaCommConfig(): QaCommTarget {
  return IS_PROD ? qaComm.prod : qaComm.dev;
}

// ── QA 모드 ON/OFF 스토어 (localStorage + 커스텀 이벤트) ─────────────
const QA_MODE_KEY = 'qa-feedback-mode';
export const QA_MODE_CHANGED_EVENT = 'qa-mode-changed';
export const QA_CAPTURE_EVENT = 'qa-capture-request';
export const QA_AREA_CAPTURE_EVENT = 'qa-area-capture-request';

/**
 * QA 모드 활성 여부. dev 빌드에서는 별도 저장값이 없으면 기본 ON(dev 는 QA 전용 서버).
 * 운영 빌드에서는 항상 false.
 */
export function isQaModeEnabled(): boolean {
  if (!IS_QA_ELIGIBLE) return false;
  try {
    const saved = localStorage.getItem(QA_MODE_KEY);
    if (saved === null) return true; // dev 기본 ON
    return saved === 'true';
  } catch {
    return true;
  }
}

/** QA 모드 ON/OFF 설정 + 구독자에게 브로드캐스트 */
export function setQaModeEnabled(enabled: boolean): void {
  if (!IS_QA_ELIGIBLE) return;
  try {
    localStorage.setItem(QA_MODE_KEY, enabled ? 'true' : 'false');
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent(QA_MODE_CHANGED_EVENT, { detail: enabled }));
}

/** QA 모드 변경 구독 (배지 ↔ 트리거 동기화용). 해제 함수 반환 */
export function subscribeQaMode(cb: (enabled: boolean) => void): () => void {
  const handler = () => cb(isQaModeEnabled());
  window.addEventListener(QA_MODE_CHANGED_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(QA_MODE_CHANGED_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

/**
 * Radix 오버레이(Popover/DropdownMenu/Dialog/Sheet)의 outside-interaction dismiss 에서
 * QA 버전 배지·QA 팝오버(`data-screenshot-exclude` 요소) 조작을 예외 처리.
 * 프로필 드롭다운 등 오버레이를 연 채로 배지를 눌러 QA 캡처를 할 수 있게 한다.
 * 운영 빌드에는 해당 attribute 요소가 렌더되지 않으므로 no-op.
 */
export function preventOverlayDismissForQaCapture(event: {
  target: EventTarget | null;
  detail?: { originalEvent?: Event };
  preventDefault: () => void;
}): void {
  const target = (event.detail?.originalEvent?.target ?? event.target) as HTMLElement | null;
  if (target?.closest?.('[data-screenshot-exclude]')) event.preventDefault();
}

/** 배지 팝오버 → 전역 트리거에 캡처 요청 (전체 화면) */
export function requestQaCapture(): void {
  window.dispatchEvent(new CustomEvent(QA_CAPTURE_EVENT));
}

/** 배지 팝오버 → 전역 트리거에 영역 캡처 요청 */
export function requestQaAreaCapture(): void {
  window.dispatchEvent(new CustomEvent(QA_AREA_CAPTURE_EVENT));
}
